import React, { useMemo } from 'react';
import { Typography } from '@duxton-web/ui';
import { Divider, Tooltip } from 'antd';
import dayjs from 'dayjs';

import { convertSliceAssignmentsToAllocationData } from '@/features/experiment/utils';
import { SliceAssignment } from '@/lib/PlannerDTOs';
import ColorIndicator from '@/pages/ExperimentFormV2/common/ColorIndicator';
import {
  CardTitle,
  TreatmentName,
} from '@/pages/ExperimentFormV2/common/TreatmentCard';

interface AllocationCalendarProps {
  subtitle?: string;
  unixStart: number;
  unixEnd: number;
  blockCountEachHour?: number;
  sliceAssignments?: SliceAssignment[];
}

const DEFAULT_BLOCK_COUNT_EACH_HOUR = 1;

const AllocationCalendar = ({
  subtitle,
  unixStart,
  unixEnd,
  blockCountEachHour = DEFAULT_BLOCK_COUNT_EACH_HOUR,
  sliceAssignments,
}: AllocationCalendarProps) => {
  const blockCountEachDay = blockCountEachHour * 24;
  const dayCount = 1 + dayjs.unix(unixEnd).diff(dayjs.unix(unixStart), 'days');

  const allocationData = useMemo(
    () =>
      convertSliceAssignmentsToAllocationData(
        sliceAssignments,
        unixStart,
        blockCountEachHour,
      ),
    [sliceAssignments, unixStart, blockCountEachHour],
  );

  // get day of startTimestamp by dayjs
  const startDay = dayjs.unix(unixStart);

  const getHourTitleFromBlockIndex = (blockIndex: number) => {
    const startingHour = Math.floor(blockIndex / blockCountEachHour);
    const startingMinute = Math.floor(
      ((blockIndex % blockCountEachHour) * 60) / blockCountEachHour,
    );
    const startingTime = `${startingHour
      .toString()
      .padStart(2, '0')}:${startingMinute.toString().padStart(2, '0')}`;
    let endingHour = startingHour;
    let endingMinute = Math.floor(
      (((blockIndex % blockCountEachHour) + 1) * 60) / blockCountEachHour,
    );
    if (endingMinute === 60) {
      endingHour += 1;
      endingMinute = 0;
    }
    const endingTime = `${endingHour.toString().padStart(2, '0')}:${endingMinute
      .toString()
      .padStart(2, '0')}`;
    return `${startingTime} - ${endingTime}`;
  };

  const displayData = useMemo<{ name: string; color: string }[][]>(() => {
    if (!allocationData) return [];
    let res = [];
    for (let i = 0; i < dayCount; i++) {
      res[i] = [];

      const dayAllocation = allocationData.map((data) => {
        if (data.allocation.find((a) => a.dayIndex === i)) {
          return {
            name: data.name,
            color: data.color,
            blockIndexes:
              data.allocation.find((a) => a.dayIndex === i)?.blockIndexes || [],
          };
        }
      });
      for (let j = 0; j < blockCountEachDay; j++) {
        const blockData = dayAllocation.find((a) =>
          a?.blockIndexes.includes(j),
        );
        res[i][j] = blockData
          ? { name: blockData.name, color: blockData.color }
          : null;
      }
    }
    res = res.filter((dayData) => dayData.some((blockData) => !!blockData));
    return res;
  }, [allocationData, dayCount]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Typography.Text strong>Allocation</Typography.Text>
      {subtitle && (
        <div
          style={{
            color: '#707070',
            fontSize: '12px',
          }}
        >
          {subtitle}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: 8,
          gap: 8,
          border: '1px solid #E8E8E8',
          borderRadius: 8,
          width: 'fit-content',
        }}
      >
        {allocationData.map((item, index) => (
          <>
            <CardTitle
              style={{
                gap: 4,
              }}
            >
              <ColorIndicator color={item.color} />
              <TreatmentName>{item.name}</TreatmentName>
            </CardTitle>
            {index !== allocationData.length - 1 && (
              <Divider
                type="vertical"
                style={{ margin: 0, color: '#E8E8E8' }}
              />
            )}
          </>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${blockCountEachDay + 1}, 1fr)`,
            gap: 2,
          }}
        >
          <div
            style={{
              border: 'none',
              textAlign: 'center',
              color: '#A3A3A3',
              fontWeight: 700,
              gridColumnStart: 1,
              gridColumnEnd: 2,
              width: 30,
            }}
          />
          <div
            style={{
              gridColumnStart: 2,
              gridColumnEnd: blockCountEachDay + 2,
              display: 'grid',
              gridTemplateColumns: `repeat(24, 1fr)`,
              gap: 2,
            }}
          >
            {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
              <div
                key={hour}
                style={{
                  border: 'none',
                  textAlign: 'center',
                  color: '#A3A3A3',
                  fontWeight: 700,
                }}
              >
                {hour}
              </div>
            ))}
          </div>
        </div>
        {Array.from({ length: displayData.length }, (_, i) => i).map(
          (dayIndex) => (
            <div
              key={`allocation-calendar-day-${dayIndex}`}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${blockCountEachDay + 1}, 1fr)`,
              }}
            >
              <div
                style={{
                  border: 'none',
                  textAlign: 'center',
                  color: '#A3A3A3',
                  fontWeight: 700,
                  gridColumnStart: 1,
                  gridColumnEnd: 2,
                  width: 30,
                }}
              >
                D{dayIndex + 1}
              </div>
              <div
                style={{
                  gridColumnStart: 2,
                  gridColumnEnd: blockCountEachDay + 2,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${blockCountEachDay}, 1fr)`,
                  gap: 2,
                }}
              >
                {Array.from({ length: blockCountEachDay }, (_, j) => j).map(
                  (blockIndex) => {
                    const blockData = displayData?.[dayIndex]?.[blockIndex];
                    return (
                      <Tooltip
                        key={`allocation-calendar-block-${dayIndex}-${blockIndex}`}
                        title={`${startDay
                          .add(dayIndex, 'day')
                          .format('YYYY/M/D')}: ${getHourTitleFromBlockIndex(
                          blockIndex,
                        )} ${startDay.format('(Z)')}`}
                        destroyTooltipOnHide
                      >
                        <div
                          key={blockIndex}
                          style={{
                            border: 'none',
                            textAlign: 'center',
                            background:
                              blockData?.color ||
                              'repeating-linear-gradient(135deg,white,white 5px,#E8E8E8 5px,#E8E8E8 10px)',
                          }}
                        />
                      </Tooltip>
                    );
                  },
                )}
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default AllocationCalendar;
